import {
  AdditiveBlending,

  BufferGeometry,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  NormalBlending,
  PlaneGeometry,
  ShaderMaterial,
  Vector3,
  type PerspectiveCamera,
  type Scene,
} from 'three';

/**
 * Šplouchání ve vodě — kapky a kruhy na hladině.
 *
 * Celé to jede na GPU z jediného draw callu na každý druh efektu. Instance
 * se recyklují v kruhu a doba života se počítá výhradně z času ve shaderu,
 * takže na CPU nepřipadá žádná práce za snímek kromě jednoho uniformu.
 * Jinými slovy: šplouchnutí navíc nic nestojí, ať jich je jedno nebo sto.
 *
 * Kapky jsou natočené čelem ke kameře až ve vertex shaderu. Kdyby se
 * billboardovaly na CPU, znamenal by každý snímek přepis celé matice pro
 * všechny instance — přesně to, co se u částic dělat nemá.
 */

/** Kolik kruhů na hladině může běžet naráz. */
const RIPPLE_COUNT = 96;
/** Kolik kapek může být ve vzduchu naráz. */
const DROPLET_COUNT = 512;
/** Kolik kapek vypustí jedno šplouchnutí při plné síle. */
const DROPLETS_PER_SPLASH = 26;
const RIPPLE_LIFETIME = 1.9;
const DROPLET_LIFETIME = 1.1;

export class Splashes {
  private readonly ripples: Mesh;
  private readonly droplets: Mesh;
  private readonly uniforms = { uTime: { value: 0 }, uCamRight: { value: new Vector3() }, uCamUp: { value: new Vector3() } };

  private rippleCursor = 0;
  private dropletCursor = 0;

  private readonly rippleStart: InstancedBufferAttribute;
  private readonly ripplePos: InstancedBufferAttribute;
  private readonly rippleParams: InstancedBufferAttribute;
  private readonly rippleSeed: InstancedBufferAttribute;
  private readonly dropletStart: InstancedBufferAttribute;
  private readonly dropletOrigin: InstancedBufferAttribute;
  private readonly dropletVel: InstancedBufferAttribute;

  constructor(scene: Scene, private readonly seaLevel: number) {
    // --- kruhy na hladině ---
    const rippleGeom = instanced(new PlaneGeometry(1, 1).rotateX(-Math.PI / 2), RIPPLE_COUNT);
    this.rippleStart = filled(RIPPLE_COUNT, 1, -1000);
    this.ripplePos = filled(RIPPLE_COUNT, 3, 0);
    this.rippleParams = filled(RIPPLE_COUNT, 4, 0); // poloměr, síla, protažení, natočení
    this.rippleSeed = filled(RIPPLE_COUNT, 1, 0);
    rippleGeom.setAttribute('aStart', this.rippleStart);
    rippleGeom.setAttribute('aPos', this.ripplePos);
    rippleGeom.setAttribute('aParams', this.rippleParams);
    rippleGeom.setAttribute('aSeed', this.rippleSeed);

    this.ripples = new Mesh(
      rippleGeom,
      new ShaderMaterial({
        uniforms: this.uniforms,
        vertexShader: RIPPLE_VERT,
        fragmentShader: RIPPLE_FRAG,
        transparent: true,
        depthWrite: false,
        blending: NormalBlending,
      }),
    );
    this.ripples.name = 'splash_ripples';
    this.ripples.frustumCulled = false;
    this.ripples.renderOrder = 3;
    scene.add(this.ripples);

    // --- kapky ---
    const dropGeom = instanced(new PlaneGeometry(1, 1), DROPLET_COUNT);
    this.dropletStart = filled(DROPLET_COUNT, 1, -1000);
    this.dropletOrigin = filled(DROPLET_COUNT, 3, 0);
    this.dropletVel = filled(DROPLET_COUNT, 4, 0); // xyz rychlost, w velikost
    dropGeom.setAttribute('aStart', this.dropletStart);
    dropGeom.setAttribute('aOrigin', this.dropletOrigin);
    dropGeom.setAttribute('aVel', this.dropletVel);

    this.droplets = new Mesh(
      dropGeom,
      new ShaderMaterial({
        uniforms: this.uniforms,
        vertexShader: DROPLET_VERT,
        fragmentShader: DROPLET_FRAG,
        transparent: true,
        depthWrite: false,
        blending: AdditiveBlending,
      }),
    );
    this.droplets.name = 'splash_droplets';
    this.droplets.frustumCulled = false;
    this.droplets.renderOrder = 4;
    scene.add(this.droplets);
  }

  /**
   * Dopad do vody: kruh i sprška kapek.
   *
   * @param strength 0–1, odvozeno z rychlosti dopadu
   */
  splash(x: number, z: number, strength: number): void {
    const s = clamp01(strength);
    if (s <= 0.01) return;

    this.ripple(x, z, 0.9 + s * 3.4, 0.5 + s * 0.5);
    // Dopad stříká do výšky a na všechny strany stejně.
    this.spray(x, z, Math.max(3, Math.round(DROPLETS_PER_SPLASH * s)), {
      up: [2.2 * (0.45 + s), 5.8 * (0.45 + s)],
      spread: [0.6 * (0.4 + s), 3.0 * (0.4 + s)],
      size: [0.07, 0.23],
    });
  }

  /**
   * Brodění — jiný jev než dopad, a musí i jinak vypadat.
   *
   * Když se pro chůzi ve vodě použije zeslabený dopad, kapky pořád vyletí
   * skoro půl metru do vzduchu a kruhy zůstanou dokonalé kružnice v
   * pravidelném sledu. Chůze ale vodu hlavně **rozhrnuje**: nízké cákance
   * dopředu a rozvlněná brázda, ne fontána.
   *
   * @param dirX,dirZ směr pohybu (normalizovaný)
   */
  wade(x: number, z: number, dirX: number, dirZ: number, strength: number): void {
    const s = clamp01(strength);
    if (s <= 0.01) return;

    // Brázda je protažená po směru chůze a natočená podle něj.
    const angle = Math.atan2(dirZ, dirX);
    this.ripple(x, z, 0.7 + s * 1.1, 0.30 + s * 0.35, 1.5 + s * 0.9, angle);

    // Nízko a hlavně dopředu — proti dopadu desetina výšky.
    this.spray(x, z, Math.max(2, Math.round(7 * s)), {
      up: [0.35, 1.05],
      spread: [0.3, 1.1],
      size: [0.035, 0.085],
      biasX: dirX * (0.6 + s * 1.1),
      biasZ: dirZ * (0.6 + s * 1.1),
    });
  }

  private spray(
    x: number,
    z: number,
    count: number,
    o: {
      up: [number, number];
      spread: [number, number];
      size: [number, number];
      biasX?: number;
      biasZ?: number;
    },
  ): void {
    for (let i = 0; i < count; i++) {
      const index = this.dropletCursor;
      this.dropletCursor = (this.dropletCursor + 1) % DROPLET_COUNT;

      // Náhodný rozptyl; dokonalý kužel vypadá jako fontána, ne jako voda.
      const a = Math.random() * Math.PI * 2;
      const spread = rand(o.spread);

      this.dropletOrigin.setXYZ(index, x, this.seaLevel, z);
      this.dropletVel.setXYZW(
        index,
        Math.cos(a) * spread + (o.biasX ?? 0),
        rand(o.up),
        Math.sin(a) * spread + (o.biasZ ?? 0),
        rand(o.size),
      );
      this.dropletStart.setX(index, this.uniforms.uTime.value);
    }
    this.dropletStart.needsUpdate = true;
    this.dropletOrigin.needsUpdate = true;
    this.dropletVel.needsUpdate = true;
  }

  /**
   * Kruh na hladině.
   *
   * `stretch` protáhne kruh v ose `angle` — bez toho vzniká za chodcem řada
   * dokonalých soustředných kružnic, což je na první pohled počítačové.
   */
  ripple(x: number, z: number, radius: number, strength: number, stretch = 1, angle = 0): void {
    const index = this.rippleCursor;
    this.rippleCursor = (this.rippleCursor + 1) % RIPPLE_COUNT;

    this.ripplePos.setXYZ(index, x, this.seaLevel, z);
    this.rippleParams.setXYZW(index, radius, clamp01(strength), stretch, angle);
    // Semeno rozhýbe obrys, ať každý kruh vypadá jinak.
    this.rippleSeed.setX(index, Math.random() * 100);
    this.rippleStart.setX(index, this.uniforms.uTime.value);

    this.ripplePos.needsUpdate = true;
    this.rippleParams.needsUpdate = true;
    this.rippleSeed.needsUpdate = true;
    this.rippleStart.needsUpdate = true;
  }

  update(elapsed: number, camera: PerspectiveCamera): void {
    this.uniforms.uTime.value = elapsed;
    // Osy kamery pro billboard kapek. Brát je z matice je levnější než
    // lookAt na každou instanci.
    camera.matrixWorld.extractBasis(
      this.uniforms.uCamRight.value,
      this.uniforms.uCamUp.value,
      new Vector3(),
    );
  }

  dispose(): void {
    for (const mesh of [this.ripples, this.droplets]) {
      mesh.removeFromParent();
      mesh.geometry.dispose();
      (mesh.material as ShaderMaterial).dispose();
    }
  }
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

function rand([min, max]: [number, number]): number {
  return min + Math.random() * (max - min);
}

function instanced(source: BufferGeometry, count: number): InstancedBufferGeometry {
  const geometry = new InstancedBufferGeometry();
  geometry.index = source.index;
  geometry.attributes = source.attributes;
  geometry.instanceCount = count;
  return geometry;
}

function filled(count: number, size: number, value: number): InstancedBufferAttribute {
  const array = new Float32Array(count * size).fill(value);
  return new InstancedBufferAttribute(array, size);
}

const LIFE_HEAD = /* glsl */ `
uniform float uTime;
varying float vLife;
varying vec2 vUv;
`;

const RIPPLE_VERT = /* glsl */ `
${LIFE_HEAD}
attribute float aStart;
attribute vec3 aPos;
attribute vec4 aParams;
attribute float aSeed;
varying float vStrength;
varying float vSeed;

void main() {
  float age = uTime - aStart;
  vLife = clamp(age / ${RIPPLE_LIFETIME.toFixed(1)}, 0.0, 1.0);
  vStrength = aParams.y;
  vSeed = aSeed;

  // Kruh se rozjíždí rychle a pak zpomaluje, jako opravdová vlnka.
  float radius = aParams.x * (1.0 - pow(1.0 - vLife, 2.2));

  // Protažení po směru chůze. Brázda za brodícím se člověkem není řada
  // soustředných kružnic, ale vlny odhrnuté do stran.
  float ca = cos(aParams.w);
  float sa = sin(aParams.w);
  vec2 local = vec2(position.x, position.z);
  vec2 shaped = vec2(local.x * ca - local.y * sa, local.x * sa + local.y * ca);
  shaped.x *= max(aParams.z, 0.001);

  vUv = uv;
  vec3 world = aPos + vec3(shaped.x * radius * 2.0, 0.02, shaped.y * radius * 2.0);
  // Doběhlé instance se odsunou mimo záběr, ať nezabírají fill rate.
  if (age < 0.0 || vLife >= 1.0) world = vec3(0.0, -10000.0, 0.0);

  gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
}
`;

const RIPPLE_FRAG = /* glsl */ `
${LIFE_HEAD}
varying float vStrength;
varying float vSeed;

void main() {
  // Prstenec, ne kotouč — plná placka by na hladině vypadala jako talíř.
  // Tenký a jemný: šířka se počítá v podílu poloměru, takže rostoucí kruh
  // se zároveň ztenčuje.
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  // Široká měkká stopa, ne ostrá linka. Tenký prstenec s rozvlněným
  // obrysem začne vypadat jako drátěná smyčka ležící na vodě.
  float width = mix(0.26, 0.10, vLife);

  // Rozvlněný obrys. Přesná kružnice je to, co na těchhle efektech oko
  // odhalí první — pár harmonických podle úhlu ji rozbije, a protože má
  // každá instance jiné semeno, žádné dva kruhy nevypadají stejně.
  float th = atan(p.y, p.x);
  float wobble = 1.0
    + 0.055 * sin(3.0 * th + vSeed)
    + 0.032 * sin(5.0 * th - vSeed * 1.7)
    + 0.018 * sin(8.0 * th + vSeed * 0.6);
  float ring = smoothstep(width, 0.0, abs(r - 0.86 * wobble));

  // Náběh na začátku, ať se kroužek neobjeví naráz v plné síle.
  float fade = smoothstep(0.0, 0.08, vLife) * (1.0 - smoothstep(0.22, 0.85, vLife));
  float alpha = ring * fade * vStrength * 0.34;
  if (alpha < 0.004) discard;

  gl_FragColor = vec4(vec3(0.96, 0.99, 1.0), alpha);
}
`;

const DROPLET_VERT = /* glsl */ `
${LIFE_HEAD}
uniform vec3 uCamRight;
uniform vec3 uCamUp;
attribute float aStart;
attribute vec3 aOrigin;
attribute vec4 aVel;

void main() {
  float age = uTime - aStart;
  vLife = clamp(age / ${DROPLET_LIFETIME.toFixed(1)}, 0.0, 1.0);

  // Balistika: kapka letí a padá zpátky do vody.
  vec3 world = aOrigin + aVel.xyz * age + vec3(0.0, -9.81 * 0.5 * age * age, 0.0);
  float size = aVel.w * (1.0 - vLife * 0.45);

  vUv = uv;
  world += uCamRight * position.x * size + uCamUp * position.y * size;
  if (age < 0.0 || vLife >= 1.0 || world.y < aOrigin.y - 0.3) world = vec3(0.0, -10000.0, 0.0);

  gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
}
`;

const DROPLET_FRAG = /* glsl */ `
${LIFE_HEAD}

void main() {
  // Měkká kulička. Ostrý čtvereček by při aditivním míchání byl okamžitě
  // vidět jako čtverec.
  float r = length(vUv * 2.0 - 1.0);
  float shape = smoothstep(1.0, 0.15, r);
  float alpha = shape * (1.0 - vLife) * 0.85;
  if (alpha < 0.004) discard;

  gl_FragColor = vec4(vec3(0.90, 0.96, 1.0), alpha);
}
`;
